import type { HelmAirService } from '../../lib/api/types'

/**
 * How an air shipment flies, in the team's words. Charter is a whole aircraft,
 * or several, for one customer's cargo: governments, aid agencies, projects
 * and private clients with loads too big, urgent or sensitive for scheduled
 * flights.
 */
export const AIR_SERVICES: ReadonlyArray<{
  value: HelmAirService
  label: string
  description: string
}> = [
  {
    value: 'STANDARD',
    label: 'Air freight',
    description: 'Consolidated on scheduled flights',
  },
  { value: 'EXPRESS', label: 'Air express', description: 'The fastest scheduled service' },
  { value: 'COURIER', label: 'Courier', description: 'Door to door with a courier' },
  {
    value: 'CHARTER',
    label: 'Air charter',
    description: 'A whole aircraft, or several, for this cargo alone',
  },
]

const AIR_LABELS = Object.fromEntries(
  AIR_SERVICES.map((service) => [service.value, service.label]),
) as Record<HelmAirService, string>

/** "Air charter", "Air express", "Air", "Sea", "Road" or "Not set". */
export function shipmentLabel(
  method: string | null | undefined,
  airService: HelmAirService | null | undefined,
) {
  switch ((method ?? '').toUpperCase()) {
    case 'AIR':
      return airService ? AIR_LABELS[airService] : 'Air'
    case 'SEA':
      return 'Sea'
    case 'ROAD':
    case 'TRUCK':
      return 'Road'
    default:
      return 'Not set'
  }
}

/** One choice for how a shipment travels: a method, and for air how it flies. */
export type ShipmentChoice = 'NONE' | 'AIR' | `AIR:${HelmAirService}` | 'SEA' | 'ROAD'

export const SHIPMENT_CHOICES: ReadonlyArray<{ value: ShipmentChoice; label: string }> = [
  { value: 'NONE', label: 'Not known yet' },
  ...AIR_SERVICES.map((service) => ({
    value: `AIR:${service.value}` as ShipmentChoice,
    label: service.label,
  })),
  { value: 'AIR', label: 'Air, service not decided' },
  { value: 'SEA', label: 'Sea' },
  { value: 'ROAD', label: 'Road' },
]

export function shipmentChoice(
  method: string | null | undefined,
  airService: HelmAirService | null | undefined,
): ShipmentChoice {
  const upper = (method ?? '').toUpperCase()
  if (upper === 'AIR') return airService ? `AIR:${airService}` : 'AIR'
  if (upper === 'SEA' || upper === 'ROAD') return upper
  return 'NONE'
}

/** The fields a choice writes: `shipping_method` and `air_service` together. */
export function shipmentFields(choice: ShipmentChoice): {
  shipping_method: 'AIR' | 'SEA' | 'ROAD' | null
  air_service: HelmAirService | null
} {
  if (choice === 'NONE') return { shipping_method: null, air_service: null }
  if (choice.startsWith('AIR:')) {
    return { shipping_method: 'AIR', air_service: choice.slice(4) as HelmAirService }
  }
  return { shipping_method: choice as 'AIR' | 'SEA' | 'ROAD', air_service: null }
}
